import { useCallback, useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { clientRequestHeaders } from "@/lib/sm-api";
import {
  tenantPublicToggleConfirmMessage,
  type TenantPublicStatus,
} from "@structura/core/tenant-public-status";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type PublicStatusPayload = {
  publicStatus: {
    status: TenantPublicStatus;
    offlineReason?: string | null;
  };
};

export function TenantPublicStatusSwitch({
  tenantId,
  compact = false,
  className,
}: {
  tenantId: string;
  compact?: boolean;
  className?: string;
}) {
  const [status, setStatus] = useState<TenantPublicStatus>("online");
  const [loading, setLoading] = useState(true);
  const [pendingOffline, setPendingOffline] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await fetch("/api/tenant/public-status", {
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
      });
      const data = (await res.json()) as PublicStatusPayload & { error?: string };
      if (res.ok && data.publicStatus) {
        setStatus(data.publicStatus.status === "offline" ? "offline" : "online");
      }
    } catch {
      /* keep last known */
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyStatus = async (next: TenantPublicStatus) => {
    const prev = status;
    setStatus(next);
    setBusy(true);
    try {
      const res = await fetch("/api/tenant/public-status", {
        method: "PATCH",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
      });
      const data = (await res.json()) as PublicStatusPayload & { error?: string };
      if (!res.ok || !data.publicStatus) {
        setStatus(prev);
        toast.error(data.error ?? "No se pudo actualizar la disponibilidad");
        return;
      }
      setStatus(data.publicStatus.status === "offline" ? "offline" : "online");
      toast.success(
        data.publicStatus.status === "online"
          ? "Tienda pública activa"
          : "Tienda pública pausada",
      );
    } catch {
      setStatus(prev);
      toast.error("Error de red");
    } finally {
      setBusy(false);
      setPendingOffline(false);
    }
  };

  const onCheckedChange = (checked: boolean) => {
    const next: TenantPublicStatus = checked ? "online" : "offline";
    if (next === "offline") {
      setPendingOffline(true);
      return;
    }
    void applyStatus("online");
  };

  const online = status === "online";

  return (
    <>
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg border border-border/80 bg-card/80 px-2.5 py-1.5",
          compact && "px-2 py-1",
          className,
        )}
        title="Disponibilidad de la tienda pública"
      >
        <Switch
          id="tenant-public-status"
          checked={online}
          disabled={loading || busy || !tenantId}
          onCheckedChange={onCheckedChange}
          className="data-[state=checked]:bg-emerald-600"
        />
        <Label
          htmlFor="tenant-public-status"
          className={cn(
            "cursor-pointer text-xs font-medium leading-none",
            online ? "text-emerald-400" : "text-amber-400",
            compact && "hidden sm:inline",
          )}
        >
          {loading ? "…" : online ? "Online" : "Offline"}
        </Label>
      </div>

      <AlertDialog open={pendingOffline} onOpenChange={(o) => !o && setPendingOffline(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pausar tienda pública</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {tenantPublicToggleConfirmMessage("fm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                void applyStatus("offline");
              }}
            >
              Pausar tienda
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
